import { array, option, predicate } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { Amount, Restriction } from '@mews-commander/data-access';
import { TimeUnit } from '@mews/enums';
import {
    ADVANCE_EXCEPTION_TYPE,
    LENGTH_EXCEPTION_TYPE,
    PRICE_EXCEPTION_TYPE,
    RESERVATION_COUNT_EXCEPTION_TYPE,
} from '../../constants';
import { Range } from '../../types';
import processExceptionValues from './processExceptionValues';
import reduceToMostRestrictingExceptions from './reduceToMostRestrictingExceptions';
import reshapeExceptionsObject from './reshapeExceptionsObject';

export type ReshapedExceptions = ReturnType<typeof reshapeExceptionsObject>;

export const convertAllExceptionsValues = ({
    restrictions,
    timeUnit,
}: {
    restrictions: Array<Pick<Restriction, 'exceptions'>>;
    timeUnit?: TimeUnit;
    hasOpenHoursIntervals?: boolean;
}) =>
    pipe(
        restrictions,
        array.map((restriction) => processExceptionValues(restriction, timeUnit))
    );

export const isValidNumberRange = (range: Range<number> | undefined) =>
    (range?.min ?? 0) <= (range?.max ?? Number.POSITIVE_INFINITY);

export const isValidPriceRange = (range: Range<Amount> | undefined) =>
    isValidNumberRange({ min: range?.min?.value, max: range?.max?.value });

const areAllNumberRangesConsistent = flow(
    (reshapedException: ReshapedExceptions) => [
        reshapedException[ADVANCE_EXCEPTION_TYPE],
        reshapedException[LENGTH_EXCEPTION_TYPE],
        reshapedException[RESERVATION_COUNT_EXCEPTION_TYPE],
    ],
    array.every(isValidNumberRange)
);

const isPriceRangeConsistent = flow(
    (reshapedException: ReshapedExceptions) => reshapedException[PRICE_EXCEPTION_TYPE],
    isValidPriceRange
);

const getCellExceptions = flow(
    convertAllExceptionsValues,
    option.fromPredicate(array.isNonEmpty),
    option.filter(
        array.every(
            flow(
                Object.values,
                array.some((value) => value !== null)
            )
        )
    ),
    option.flatMap(reduceToMostRestrictingExceptions),
    option.map(reshapeExceptionsObject),
    option.filter(predicate.and(areAllNumberRangesConsistent)(isPriceRangeConsistent)),
    option.getOrElse<Partial<ReshapedExceptions>>(() => ({}))
);

export default getCellExceptions;
